/**
 * Topic discovery: the dialog, both its doors, the result cards and the tuner
 * (NEWS-297; the feature is NEWS-124–128; kerf UI, NM-22W019).
 *
 * Seventh seam out of `app.tsx`. It looked like one of the "stateful dialogs"
 * that would need untangling first, and it does not — because the state machine
 * was already lifted out in NEWS-127. `startTuner`/`judgeCandidate`/`nextRound`/
 * `mergeKept` live in `discover.ts` as pure functions, the session lives in the
 * store, and the request-issuing handlers (`runDiscovery`, `fetchTunerRound`,
 * `enterTuner`, `judgeTunerCandidate`, `finishTuner`, `loadMoreSuggestions`)
 * stayed in `app.tsx` with every other `delegate()`. What was left here is
 * markup, which is why this moved as cleanly as the stateless dialogs did.
 *
 * The frame is kerf UI's `DialogSurface` (a native `<dialog>`), like
 * every other dialog (`dialogs.tsx`), but composed directly rather than through
 * `appDialog`: this one needs a definite height (so the box does not jump as
 * the user moves between panes, NM-36) and a `fill-last` body, which keeps the
 * search box pinned while the pane beneath it scrolls. kerf UI owns the
 * modal behaviour — top layer, inert page, Escape, the header close button and
 * light dismiss — and the dialog `close` listener in `app.tsx` clears the
 * store's `discover` state by the dialog's `data-dialog` key.
 *
 * Rendering only: `discover-nav`, `discover-search`, `data-tuner`,
 * `data-add-suggestion`, `discover-back` and the rest are handled by
 * `delegate()` in `app.tsx` (NEWS-126).
 */

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Chip } from '@kerfjs/ui/chip';
import { ContentItem } from '@kerfjs/ui/content-item';
import { px } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { LoadingSpinner } from '@kerfjs/ui/loading-spinner';
import { Row } from '@kerfjs/ui/row';
import { Skeleton } from '@kerfjs/ui/skeleton';
import { Spacer } from '@kerfjs/ui/spacer';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';
import { each } from 'kerfjs';

import type { TopicSuggestion } from '../../../api/schemas.js';
import { MAX_DISCOVER_QUERY_LENGTH, MAX_TUNE_ROUNDS } from '../../../api/schemas.js';
import { topicsForProfiles } from '../../../profile-topics.js';
import { profileLabels } from '../../../profiles.js';
import { evergreenTopicsForSection, hasEvergreenTopics } from '../../../section-topics.js';
import type { DiscoverState } from '../../app/stores.js';
import { appStore } from '../../app/stores.js';
import { icon } from '../../components/icons.js';
import type { TunerState } from './discover.js';
import {
  currentCandidate,
  dedupeSuggestionsAgainst,
  groupSuggestions,
  kindLabel,
  resultsHeading,
  resultsQualifier,
  sectionFor,
  sectionTiles,
  tunerRationale,
} from './discover.js';
import { animationDurationMs, DEFAULT_TARGET_MS, estimateTargetMs, readDurations } from './discover-progress.js';

/**
 * Topic discovery (NEWS-126, `docs/24-topic-discovery.md`).
 *
 * Two doors into one result list, and deliberately neither is primary: the box
 * serves someone who sort of knows what they're into, the section grid serves
 * someone who wants to see what exists, and each covers the other's failure. An
 * empty box is "surprise me" (FR-24.3), not an error — which is what stops the
 * blank field being a wall for the very user this feature is for.
 */
export function discoverDialogJsx(d: DiscoverState): SafeHtml {
  return (
    <DialogSurface
      label="Discover Topics"
      rootAttributes={{ 'data-key': 'dialog-discover', 'data-dialog': 'discover' }}
      size="large"
      height={px(680)}
      bodyLayout="fill-last"
      lightDismiss
    >
      <form data-action="discover-search" data-discover-search="">
        <List>
          {/* The input grows; the button keeps its size beside it. */}
          <Row gap="xs" vAlign="bottom" controlInsets="rl">
            <List flex>
              <TextField
                name="discover-query"
                label="What Are You Into?"
                placeholder="e.g. cycling and biotech"
                maxLength={MAX_DISCOVER_QUERY_LENGTH}
                autocomplete="off"
              />
            </List>
            <Button type="submit" tone="brand" disabled={d.loading}>
              Suggest
            </Button>
          </Row>
          {/* Minor separation between the pinned box and the pane under it. */}
          <Spacer height="m" />
        </List>
      </form>
      {/* The scrolling half. Only this scrolls, so the box stays put while a
          long result list moves under it. One always-present container holds
          every pane, so switching panes never restructures the dialog. */}
      <List scrollable gap="m" rootAttributes={{ 'data-dialog-body': '', 'data-discover-body': '' }}>
        {discoverBodyJsx(d)}
      </List>
    </DialogSurface>
  );
}

/** The one-level-up control every pane shares (NM-34, NM-118). */
function backButtonJsx(): SafeHtml {
  return (
    <Button emphasis="plain" size="compact" action="discover-back" icon={icon('chevron-left')}>
      Back
    </Button>
  );
}

/**
 * The pane below the box: the section grid, a section's subcategories, or results.
 */
function discoverBodyJsx(d: DiscoverState): SafeHtml {
  if (d.tuner !== null) return tunerJsx(d.tuner);
  // A section drill-in that has instant evergreen picks shows them *immediately*
  // (NM-30, FR-24.41): rather than the full-pane waiting bar replacing everything
  // while the AI call runs, `discoverResultsJsx` renders the static strip now and
  // a compact inline affordance for the AI half, which the timely suggestions
  // then append below. Every other wait — a free-text query, or a section with no
  // evergreen picks — keeps the full waiting pane it had.
  if (d.loading) {
    const section = activeSectionSlug(d);
    if (section !== null && hasEvergreenTopics(section)) return discoverResultsJsx(d);
    return discoverWaitingJsx();
  }
  if (d.error !== null) {
    return (
      <div data-discover-error="">
        <StateBanner
          tone="danger"
          urgency="alert"
          icon={icon('warn')}
          title="Couldn’t Get Suggestions"
          detail={d.error}
          copyLayout="stacked"
          action={
            <Button emphasis="secondary" size="compact" action="discover-retry">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  if (d.view === 'results') return discoverResultsJsx(d);
  return d.section === null ? sectionGridJsx() : subsectionsJsx(d.section);
}

/**
 * What a discovery call looks like while it runs (NEWS-137).
 *
 * The bar is paced entirely by CSS: the estimated duration is handed over as a
 * custom property and a keyframe animation does the rest. No timer, no
 * per-frame re-render — which matters because a 10 Hz re-render of the whole
 * mount would fight the morph for a bar that is decorative by construction.
 * That is also why it is the app's own raw element rather than a
 * `wa-progress-bar`: a Web Awesome bar shows a `value` the app would have to
 * keep setting, or an indeterminate sweep that says nothing about the wait.
 *
 * It is `aria-hidden` with the status line beside it doing the announcing: a
 * progress bar whose value is an estimate has nothing truthful to report to a
 * screen reader, and "37%" would be a claim the app cannot stand behind.
 */
function discoverWaitingJsx(): SafeHtml {
  const target = estimateTargetMs(readDurations());
  return (
    <List gap="m" rootAttributes={{ 'data-discover-waiting': '' }}>
      {/* A back control while the search runs (NM-117): without it the waiting
          pane trapped the user until the answer arrived. `discover-back` clears
          the loading state and cancels the in-flight request. */}
      <Row>{backButtonJsx()}</Row>
      <List gap="xs" hAlign="center">
        <Text align="center" role="status" data-discover-status="">
          Asking…
        </Text>
        <div
          class="discover-bar"
          data-discover-bar=""
          aria-hidden="true"
          style={`--discover-duration: ${String(Math.round(animationDurationMs(target)))}ms`}
        >
          <span class="discover-bar-fill" />
        </div>
        <Text align="center" tone="quiet" size="compact" data-discover-bar-note="">
          {target === DEFAULT_TARGET_MS
            ? 'This usually takes half a minute.'
            : `Recent searches took about ${String(Math.round(target / 1000))}s.`}
        </Text>
      </List>
    </List>
  );
}

/**
 * How many profile-derived topics the strip offers.
 *
 * Six, not twelve: this is a nudge beside two other doors, not the onboarding
 * seed. A row long enough to scroll would compete with the section grid it sits
 * above, and the whole point is that it reads at a glance.
 */
const FOR_YOU_COUNT = 6;

/**
 * A one-click add chip (the profile strip and the evergreen strip). The same
 * plain `addTopic` as the empty feed's Popular Topics, so it shares that
 * delegate (`add-starter-topic`, the name as `data-item-id`): these are names
 * from a static table with no reason, kind or classification behind them, so
 * there is no suggestion to mark as added — the chip disappears on the next
 * render because the topic now excludes itself (FR-24.11).
 */
function addChipsJsx(names: readonly string[]): SafeHtml {
  return (
    <Row gap="xs" wrap controlInsets="rl">
      {names.map((name) => (
        <Chip action="add-starter-topic" itemId={name} actionLabel={`Watch ${name}`}>
          {name}
        </Chip>
      ))}
    </Row>
  );
}

/**
 * Topics drawn from the reader profiles (NEWS-406, FR-24.40).
 *
 * **Static, from the same table onboarding uses — no call, no latency, and it
 * works with no provider configured.** That is what makes it safe to render on
 * open: FR-24.16 keeps every discovery call user-initiated, and that property is
 * load-bearing because NEWS-119 left no spend cap, so "user-initiated" *is* the
 * protection. A strip that fetched on open would quietly turn opening the dialog
 * into a billable call.
 *
 * There is no "get fresh ideas" button beside it, deliberately: the box directly
 * above already does that, and since NEWS-386 an empty submission is biased by
 * the same profiles. A second control for the same request would be the reduced
 * second implementation NEWS-146 unpicked.
 *
 * Renders nothing at all when no profiles are set. This is the one part of the
 * dialog with nothing to say without them, and a hollow shell reads as a fault.
 */
function forYouJsx(): SafeHtml | null {
  const s = appStore.state.value;
  const names = topicsForProfiles(s.settings.profiles, {
    cap: FOR_YOU_COUNT,
    // FR-24.11 applies here as everywhere: never offer something already
    // followed. Static or not, a suggestion the user is already watching is the
    // most obviously-broken thing this pane could show.
    exclude: s.topics.map((t) => t.name),
  });
  if (names.length === 0) return null;
  const labels = profileLabels(s.settings.profiles);
  return (
    <List gap="xs" rootAttributes={{ 'data-discover-foryou': '' }}>
      {/* Names the profiles it is drawing on. That is what makes it read as
          personal rather than arbitrary, and it doubles as the hint that these
          are editable. */}
      <Text tone="quiet" size="compact" flush controlMargins="rl">
        Because you said you’re into {labels.join(', ').toLowerCase()}:
      </Text>
      {addChipsJsx(names)}
    </List>
  );
}

/**
 * The section tiles (FR-24.2): interactive `ContentItem`s in a reflowing `Grid`.
 * Enter and Space activate a focused tile through `wireContentItems` (app.tsx).
 *
 * `.map()`, not `each()` — the sections are a constant array, and `each()`
 * memoizes per item by object identity, so a constant list would cache forever
 * and stop re-rendering (kerf hard rule 14).
 */
function sectionGridJsx(): SafeHtml {
  return (
    <List gap="m" rootAttributes={{ 'data-discover-browse': '' }}>
      {/* Between the box and the grid (NEWS-406): the pane reads *say what you're
          into* → *here's what we think* → *or browse*. Always-present container so
          the strip appearing cannot restructure the grid below it (docs/3-ui.md). */}
      <div data-discover-slot="foryou">{forYouJsx()}</div>
      <List gap="xs">
        <Text tone="quiet" size="compact" flush controlMargins="rl">
          …or browse by section.
        </Text>
        <Grid minColumnWidth={px(150)} gap="xs" rootAttributes={{ 'data-discover-sections': '' }}>
          {sectionTiles().map((category) => (
            <ContentItem
              interactive
              action="discover-nav"
              itemId={`section:${category.slug}`}
              frame="framed"
              appearance="surface"
              rootAttributes={{ 'data-key': category.slug, 'data-discover-section': category.slug }}
            >
              <Text variant="span">{category.label}</Text>
            </ContentItem>
          ))}
        </Grid>
      </List>
    </List>
  );
}

/** One section's subcategories, plus the escape for someone who knows none of them. */
function subsectionsJsx(slug: string): SafeHtml {
  const category = sectionFor(slug);
  if (category === undefined) return sectionGridJsx();
  return (
    <List gap="m" rootAttributes={{ 'data-discover-subsections': slug }}>
      {/* "Back", consistent with the results pane's own back control (NM-34):
          each goes up exactly one level, and two differently-worded back
          buttons in one flow read as going to different places. */}
      <Row gap="xs" vAlign="middle" wrap>
        {backButtonJsx()}
        <Text variant="h3" flush data-discover-heading="">
          {category.label}
        </Text>
      </Row>
      {/* `discover-nav` with the target in `data-item-id`, exactly like the
          section tiles: one action, one delegate (docs/3-ui.md). */}
      <Row gap="xs" wrap controlInsets="rl">
        {category.subcategories.map((sub) => (
          <Chip action="discover-nav" itemId={`sub:${slug}/${sub.slug}`}>
            {sub.label}
          </Chip>
        ))}
        <Chip action="discover-nav" itemId={`sub:${slug}/`} tone="info">
          {`Anything in ${category.label}`}
        </Chip>
      </Row>
    </List>
  );
}

/**
 * Which section the current results belong to, or null (NM-30).
 *
 * Read from `source` rather than `d.section`: `source` is what the results were
 * actually *requested* for, so a free-text query returns null (no strip) even if
 * the user had a section drilled in when they typed, and a subcategory drill-in
 * resolves to its parent section — which is the level the evergreen catalog is
 * keyed at, so a subject drill-in still shows its section's floor.
 */
function activeSectionSlug(d: DiscoverState): string | null {
  return d.source !== null && d.source.kind === 'section' ? d.source.category : null;
}

/**
 * How many instant evergreen picks the strip offers (NM-30).
 *
 * Eight, a touch more than the profile strip's six: this is the pane's whole
 * content until the AI half lands, not a nudge sitting above a section grid, so a
 * slightly fuller floor reads as answered rather than sparse.
 */
const EVERGREEN_STRIP_COUNT = 8;

/**
 * The instant evergreen strip on a section drill-in (NM-30, FR-24.41).
 *
 * The static floor to the AI's timely half — the section door's realisation of
 * the "static floor + AI enhancement" pattern the profile strip already uses
 * (FR-24.40, FR-36.11). Same chips and the same one-click add as `forYouJsx`;
 * a clicked chip's topic then excludes itself on the next render (FR-24.11),
 * disappearing from the strip exactly as a profile chip does.
 */
function evergreenStripJsx(section: string, names: readonly string[]): SafeHtml {
  const label = sectionFor(section)?.label ?? 'this section';
  return (
    <List gap="xs" rootAttributes={{ 'data-discover-evergreen': section }}>
      <Text variant="h4" size="compact" tone="quiet" flush controlMargins="rl">
        Popular in {label}
      </Text>
      {addChipsJsx(names)}
    </List>
  );
}

/**
 * The compact "AI half still loading" affordance shown beneath the strip (NM-30).
 *
 * Deliberately not `discoverWaitingJsx`'s full-pane estimated bar: the strip is
 * already usable, so this only has to say the timely suggestions are on their
 * way. The status line does the announcing; the spinner and the skeleton cards
 * (NM-36, so the loading state has substance of its own) are decorative.
 */
function discoverInlineWaitingJsx(): SafeHtml {
  return (
    <List gap="xs" rootAttributes={{ 'data-discover-inline-waiting': '' }}>
      <Row gap="xs" vAlign="middle" controlInsets="rl">
        <LoadingSpinner size="s" />
        <Text variant="span" tone="quiet" role="status" data-discover-status="">
          Finding timely stories…
        </Text>
      </Row>
      <List gap="xs" rootAttributes={{ 'aria-hidden': 'true' }}>
        {[0, 1].map((i) => (
          <ContentItem frame="framed" rootAttributes={{ 'data-key': String(i) }}>
            <Skeleton lines={3} />
          </ContentItem>
        ))}
      </List>
    </List>
  );
}

/**
 * The result list (FR-24.4), grouped by section.
 *
 * The grouping doubles as a preview of where each topic will file itself in the
 * filter bar once added.
 *
 * On a section drill-in with instant picks it also renders the evergreen strip
 * above the AI results (NM-30): the strip shows the moment this pane opens, and
 * while the AI call is in flight the group list is replaced by a compact inline
 * affordance rather than the whole pane being replaced by the waiting bar. The AI
 * cards are de-duped against the names the strip already shows so nothing appears
 * twice across the two halves.
 */
function discoverResultsJsx(d: DiscoverState): SafeHtml {
  const section = activeSectionSlug(d);
  const evergreen =
    section === null
      ? []
      : evergreenTopicsForSection(section, {
          cap: EVERGREEN_STRIP_COUNT,
          // FR-24.11: never offer a topic already followed, static or not.
          exclude: appStore.state.value.topics.map((t) => t.name),
        });
  const groups = groupSuggestions(dedupeSuggestionsAgainst(d.suggestions, evergreen));
  const heading = d.source === null ? 'Suggestions' : resultsHeading(d.source);
  return (
    <List gap="m" rootAttributes={{ 'data-discover-results': '' }}>
      <Row gap="xs" vAlign="middle" wrap rootAttributes={{ 'data-discover-results-head': '' }}>
        {backButtonJsx()}
        <Text variant="h3" flush data-discover-heading="">
          {heading}
        </Text>
        {/* "closest matches" when the answers don't match the question
            (NEWS-269) — a section drill-in whose results file themselves
            somewhere else. Without it the heading and the group label below
            contradict each other and the honest reading is "the filter broke".
            Always present (empty when there is nothing to say) so its arrival
            can't disturb the keyed group list below it (kerf). */}
        <Text variant="span" tone="quiet" size="compact" data-discover-qualifier="">
          {resultsQualifier(d.source, groups)}
        </Text>
        {d.loading || d.suggestions.length === 0 || d.source === null
          ? null
          : /* Plural, and only here (NEWS-267… NEWS-265): this pair retunes the
               **whole result set**, while the identical-looking pair on each
               suggestion card retunes that one topic. Both start a six-round,
               billable tuner (FR-24.6), so guessing is expensive. "These" versus
               the card's bare "Narrower"/"Similar" is the distinction, and the
               titles name the anchor outright. Beside the heading they act on. */
            [
              <Button
                emphasis="plain"
                size="compact"
                title={`More specific than “${heading}”`}
                rootAttributes={{ 'data-tune': `narrower:${heading}`, 'data-tune-scope': 'set' }}
                icon={icon('funnel')}
              >
                Narrow These
              </Button>,
              <Button
                emphasis="plain"
                size="compact"
                title={`Adjacent to “${heading}”`}
                rootAttributes={{ 'data-tune': `similar:${heading}`, 'data-tune-scope': 'set' }}
                icon={icon('blend')}
              >
                More Like These
              </Button>,
            ]}
      </Row>
      {/* The instant evergreen strip (NM-30). Always-present slot so it collapses
          to nothing — rather than restructuring the pane — for a section with no
          picks, a free-text door, or once every pick is already followed. */}
      <div data-discover-slot="evergreen">
        {section !== null && evergreen.length > 0 ? evergreenStripJsx(section, evergreen) : null}
      </div>
      {d.loading ? (
        // The AI half is in flight: keep the strip usable above and say the
        // timely suggestions are coming, instead of showing a stale group list.
        discoverInlineWaitingJsx()
      ) : groups.length === 0 ? (
        <EmptyState title="Nothing New to Suggest" detail="You may already follow everything this turned up." />
      ) : (
        <List gap="m" rootAttributes={{ 'data-suggestion-groups': '' }}>
          {/* When the instant evergreen strip and the AI's settled results are
              both on screen they otherwise read as one continuous set (NM-32
              review). This labels the timely half so the two regions are
              explicitly parallel — "Popular in X" (static) above, "Timely
              Stories" (AI) below. Only when both are present. Always-present
              slot so its arrival can't disturb the keyed list below it. */}
          <div data-discover-slot="timely">
            {section !== null && evergreen.length > 0 ? (
              <Text variant="h4" size="compact" tone="quiet" flush controlMargins="rl">
                Timely Stories
              </Text>
            ) : null}
          </div>
          {/* Outer `each()` for the keyed group list; inner `.map()` because a
              nested `each()` is never reconciled — the row is flattened to HTML,
              so the inner list would render as static markup and silently stop
              updating. kerf throws on this in dev, which is how it was caught. */}
          {each(
            groups,
            (group) => (
              // A plain keyed root: `each()` needs `data-key` on the row's own
              // element, where the kerf lint rule can see it.
              <div data-key={group.key} data-suggestion-group={group.key}>
                <List gap="xs">
                  <Text
                    variant="h4"
                    size="compact"
                    tone="quiet"
                    flush
                    controlMargins="rl"
                    data-suggestion-group-label=""
                  >
                    {group.label}
                  </Text>
                  {group.suggestions.map((suggestion) =>
                    suggestionCardJsx(suggestion, d.added.includes(suggestion.name)),
                  )}
                </List>
              </div>
            ),
            { key: 'suggestion-groups' },
          )}
          {/* Always-present container: the button and the exhausted note swap
              in and out, and a conditional sibling must not restructure the
              keyed list above it (docs/3-ui.md). */}
          <div data-discover-more="">{discoverMoreJsx(d)}</div>
        </List>
      )}
    </List>
  );
}

/**
 * The keep/skip tuner (NEWS-127, FR-24.5–24.9).
 *
 * A **depth control**, never an entry point — that distinction is the whole
 * reason this shape was chosen over a tuner-first design. It costs nothing until
 * someone asks to go deeper, and it answers the two questions a static list
 * cannot: *narrower than this* and *more like this*.
 */
function tunerJsx(t: TunerState): SafeHtml {
  const candidate = currentCandidate(t);
  return (
    <List gap="m" rootAttributes={{ 'data-discover-tuner': '' }}>
      <Row gap="xs" vAlign="middle" controlInsets="rl">
        <Text variant="span" tone="quiet" data-tuner-round="">
          Round {String(t.round)} of {String(MAX_TUNE_ROUNDS)}
        </Text>
        <Spacer flex />
        {/* Endable at any point (FR-24.9) — and the only way out of the flow, so
            it is never hidden behind a state the user has to reach first, and it
            is a real outlined button rather than a quiet one (NEWS-266). */}
        <Button emphasis="secondary" size="compact" rootAttributes={{ 'data-tuner': 'done' }}>
          Done
        </Button>
      </Row>

      <div data-tuner-body="">{tunerCardJsx(t, candidate)}</div>

      <Text tone="quiet" size="compact" flush controlMargins="rl" data-tuner-kept="">
        {t.kept.length === 0 ? 'Nothing kept yet.' : `Kept: ${t.kept.map((s) => s.name).join(' · ')}`}
      </Text>
    </List>
  );
}

function tunerCardJsx(t: TunerState, candidate: TopicSuggestion | undefined): SafeHtml {
  if (t.error !== null) {
    return (
      <div data-discover-error="">
        <StateBanner
          tone="danger"
          urgency="alert"
          icon={icon('warn')}
          title="Couldn’t Get More Ideas"
          detail={t.error}
          copyLayout="stacked"
          action={
            <Button emphasis="secondary" size="compact" rootAttributes={{ 'data-tuner': 'done' }}>
              Back to the List
            </Button>
          }
        />
      </div>
    );
  }
  if (t.loading) return <EmptyState busy title="Thinking…" />;
  if (candidate === undefined) {
    return <EmptyState title="That’s Everything" detail="Anything you kept is waiting in the list." />;
  }
  return (
    <ContentItem frame="framed" appearance="surface" rootAttributes={{ 'data-tuner-card': candidate.name }}>
      <List gap="xs">
        {suggestionTitleJsx(candidate)}
        <Text flush>{candidate.reason}</Text>
        {/* Why this is being offered (FR-24.8). Without it the loop is a slot
            machine; with it, a user who can see the model has misread them can
            skip out rather than abandon the feature. */}
        <Text tone="quiet" size="compact" flush data-tuner-why="">
          {tunerRationale(t)}
        </Text>
        <Row gap="xs" hAlign="right" wrap>
          <Button emphasis="secondary" rootAttributes={{ 'data-tuner': 'skip' }} icon={icon('clear')}>
            Skip
          </Button>
          <Button tone="brand" rootAttributes={{ 'data-tuner': 'keep' }} icon={icon('bookmark')}>
            Keep
          </Button>
        </Row>
      </List>
    </ContentItem>
  );
}

/**
 * "More suggestions" for the whole result list (NEWS-136).
 *
 * Every press is a billable call, so when a round comes back with nothing new
 * the button is replaced by a plain statement rather than left there to be
 * pressed again — an exhausted seam should be visible, not discovered.
 */
function discoverMoreJsx(d: DiscoverState): SafeHtml {
  if (d.exhausted) {
    return (
      <Text tone="quiet" size="compact" align="center" data-discover-more-note="">
        That’s everything for this search — try another wording or section.
      </Text>
    );
  }
  return (
    <Row hAlign="center">
      <Button emphasis="secondary" action="discover-more" disabled={d.loadingMore}>
        {d.loadingMore ? 'Finding More…' : 'More Suggestions'}
      </Button>
    </Row>
  );
}

/** A suggestion's name with its catalog and ongoing/evergreen badges (FR-24.10). */
function suggestionTitleJsx(suggestion: TopicSuggestion): SafeHtml {
  return (
    <Row gap="xs" vAlign="middle" wrap>
      <Text variant="h5" flush data-suggestion-name="">
        {suggestion.name}
      </Text>
      {suggestion.catalog ? (
        <Badge
          size="compact"
          tone="info"
          title="A shared catalog topic — it doesn't count toward your custom-topic limit"
          rootAttributes={{ 'data-suggestion-catalog': '' }}
        >
          Catalog
        </Badge>
      ) : null}
      {/* Ongoing vs evergreen (FR-24.10) — the honest answer to "why did it
          suggest this?", on the card rather than hidden. */}
      <Badge
        size="compact"
        tone={suggestion.kind === 'ongoing' ? 'pop' : 'neutral'}
        rootAttributes={{ 'data-suggestion-kind': suggestion.kind }}
      >
        {kindLabel(suggestion.kind)}
      </Badge>
    </Row>
  );
}

/** One suggestion. Stays put once added — see `DiscoverState.added`. */
function suggestionCardJsx(suggestion: TopicSuggestion, added: boolean): SafeHtml {
  return (
    <ContentItem
      frame="framed"
      appearance="surface"
      rootAttributes={{
        'data-key': suggestion.name,
        'data-suggestion': suggestion.name,
        'data-suggestion-added': added ? 'true' : undefined,
      }}
    >
      <List gap="xs">
        {suggestionTitleJsx(suggestion)}
        <Text flush>{suggestion.reason}</Text>
        {/* Incentive line for catalog suggestions (NM-155): shared subjects are free
            of the custom cap and the more-followed ones are checked more often. */}
        {suggestion.catalog ? (
          <Text tone="quiet" size="compact" flush data-suggestion-incentive="">
            Shared — free of your custom-topic limit.
            {suggestion.subscribers > 0
              ? ` ${String(suggestion.subscribers)} ${suggestion.subscribers === 1 ? 'person follows' : 'people follow'} it, so it's checked more often.`
              : ''}
          </Text>
        ) : null}
        <Row gap="xs" vAlign="middle" wrap>
          {added ? (
            <Badge tone="success" icon={icon('ok')} rootAttributes={{ 'data-suggestion-added-label': '' }}>
              Added
            </Badge>
          ) : (
            /* Brand-filled: adding is the one thing to do with a card, and the
               quiet refinements beside it stay plain (NM-34 review), so one
               filled action per card reads as the obvious next step. */
            <Button tone="brand" size="compact" rootAttributes={{ 'data-add-suggestion': suggestion.name }}>
              Add
            </Button>
          )}
          <Spacer flex />
          {/* The depth controls (FR-24.5). One attribute, one delegate — see the
              delegate/morph rule in docs/3-ui.md. */}
          <Button
            emphasis="plain"
            size="compact"
            title="More specific than this"
            rootAttributes={{ 'data-tune': `narrower:${suggestion.name}`, 'data-tune-scope': 'card' }}
            icon={icon('funnel')}
          >
            Narrower
          </Button>
          <Button
            emphasis="plain"
            size="compact"
            title="Adjacent to this"
            rootAttributes={{ 'data-tune': `similar:${suggestion.name}`, 'data-tune-scope': 'card' }}
            icon={icon('blend')}
          >
            Similar
          </Button>
        </Row>
      </List>
    </ContentItem>
  );
}
